/**
 * Detects if the browser is running in an in-app browser
 */
export function isInAppBrowser() {
    if (window && window.isHBInAppBrowser) {
        return true;
    }
    return false;
}
/**
 * Detects the mobile device type from user agent
 * Returns device name or undefined if not a mobile device
 */
export function getMobileDevice() {
    const devices = {
        Android: /Android/i,
        BlackBerry: /BlackBerry/i,
        iPhone: /iPhone/i,
        iPad: /iPad/i,
        iPod: /iPod/i,
        Opera: /Opera Mini/i,
        Windows: /IEMobile/i
    };
    for (const device in devices) {
        if (navigator.userAgent.match(devices[device])) {
            return device;
        }
    }
    return undefined;
}
/**
 * Detects if the device supports touch
 */
export function isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}
/**
 * Gets mobile OS information from user agent
 */
export function getMobileOs() {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) {
        const uaindex = ua.indexOf('OS ');
        return {
            mobileOs: 'iOS',
            iosVersion: uaindex > -1
                ? ua
                    .substring(uaindex + 3, ua.indexOf(' ', uaindex + 3))
                    .replace(/_/g, '.')
                : undefined
        };
    }
    else if (/Android/.test(ua)) {
        const uaindex = ua.indexOf('Android ');
        return {
            mobileOs: 'Android',
            androidVersion: uaindex > -1
                ? ua.substring(uaindex + 8, ua.indexOf(';', uaindex + 8))
                : undefined
        };
    }
    return {};
}
/**
 * Detects if the device is running iOS
 */
export function isIOS() {
    if (isTouchDevice()) {
        return getMobileOs().mobileOs === 'iOS';
    }
    return false;
}
/**
 * Detects if the browser is Firefox
 */
export function isFireFox() {
    return navigator.userAgent.toLowerCase().includes('firefox');
}
/**
 * Detects if the browser is Facebook's in-app browser
 */
export function isFacebookBrowser() {
    return /FBAN|FBAV/i.test(navigator.userAgent);
}
/**
 * Detects if the device is a mobile device
 */
export function isMobileDevice() {
    return !!getMobileDevice();
}
/**
 * Detects if extension(s) of interest are enabled
 */
/**
 * Extracts the HoneyBook mobile app build number from the user agent.
 * UA format: "HoneyBook/<version> Android" or "HoneyBook/<version> CFNetwork"
 * Returns the last dot-segment (build number) as an integer, matching the BE logic
 * in HbUtilsModule::MobileUtil.
 */
export function getHBAppBuildNumber() {
    const ua = navigator.userAgent;
    const match = ua.match(/HoneyBook\/([\d.]+)\s+(?:Android|CFNetwork)/);
    if (!match) {
        return undefined;
    }
    const segments = match[1].split('.');
    const buildNumber = parseInt(segments[segments.length - 1], 10);
    return isNaN(buildNumber) ? undefined : buildNumber;
}
/**
 * Extracts the HoneyBook mobile app human-readable version (e.g. "3.0.55") from the user agent.
 * UA format: "HoneyBook/<readableVersion>.<buildNumber> Android|CFNetwork"
 * Returns all dot-segments except the last (which is the build number).
 */
export function getHBAppVersion() {
    const ua = navigator.userAgent;
    const match = ua.match(/HoneyBook\/([\d.]+)\s+(?:Android|CFNetwork)/);
    if (!match) {
        return undefined;
    }
    const segments = match[1].split('.');
    if (segments.length < 2) {
        return undefined;
    }
    return segments.slice(0, -1).join('.');
}
/**
 * Checks if the HoneyBook mobile app build number is at least the given minimum.
 * Pass separate thresholds for Android and iOS, matching the BE
 * `is_mobile_version_greater_than?` interface.
 *
 * Returns true if:
 * - Not in the HB mobile app (non-mobile users are not gated)
 * - The app build number meets or exceeds the threshold for its platform
 *
 * Returns false only when running in the HB app with a build number below the threshold.
 */
export function isHBAppVersionAtLeast(androidMinBuild, iosMinBuild) {
    const ua = navigator.userAgent;
    const isHBApp = isInAppBrowser();
    // Not in the HB mobile app — no gating needed
    if (!isHBApp) {
        return true;
    }
    const match = ua.match(/HoneyBook\/([\d.]+)\s+(?:Android|CFNetwork)/);
    // In the HB app but can't parse version — treat as older version
    if (!match) {
        return false;
    }
    const isAndroid = ua.includes('Android');
    const segments = match[1].split('.');
    const buildNumber = parseInt(segments[segments.length - 1], 10);
    if (isNaN(buildNumber)) {
        return false;
    }
    return buildNumber >= (isAndroid ? androidMinBuild : iosMinBuild);
}
export function isExtensionEnabled(extensionName) {
    switch (extensionName) {
        // Report when Poper Blocker (id `bkkbcggnhapdmkeljlodobbkopceiche`) is installed and we're not whitelisted, as it tends to mess up our UI
        case 'poper-blocker':
            return (!!('BetterJsPop' in window) || !!('pbExternalCommand' in window));
        default:
            return false;
    }
}
//# sourceMappingURL=device.utils.js.map